import { zodResolver } from "@hookform/resolvers/zod";
import CloseIcon from "@mui/icons-material/Close";
import {
  Alert,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { SubmitHandler, useForm } from "react-hook-form";

import { RobotoButton } from "@/shared/components/RobotoButton";
import { useUpdateLayout } from "@/shared/domain/layouts/hooks";
import {
  renameLayoutFormSchema,
  type RenameLayoutFormFields,
} from "@/shared/domain/layouts/LayoutFormModel";
import { LayoutRecord } from "@/shared/domain/layouts/LayoutRecord";
import { RobotoDomainException } from "@/shared/http/exceptions";
import { LoggerService } from "@/shared/services";

interface RenameLayoutModalProps {
  layout: LayoutRecord | null;
  isOpen: boolean;
  onClose: () => void;
  onExited: () => void;
  onSuccess: (layout: LayoutRecord) => void;
}

export function RenameLayoutModal(options: RenameLayoutModalProps) {
  const { isOpen, layout, onClose, onExited, onSuccess } = options;
  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      TransitionProps={{ onExited }}
      aria-labelledby="rename-layout-dialog-title"
      maxWidth="sm"
      fullWidth
      disableRestoreFocus
    >
      {layout && (
        <RenameLayoutForm
          layout={layout}
          onClose={onClose}
          onSuccess={onSuccess}
        />
      )}
    </Dialog>
  );
}

interface RenameLayoutFormProps {
  layout: LayoutRecord;
  onClose: () => void;
  onSuccess: (layout: LayoutRecord) => void;
}

function RenameLayoutForm({
  layout,
  onClose,
  onSuccess,
}: RenameLayoutFormProps) {
  const form = useForm<RenameLayoutFormFields>({
    defaultValues: {
      name: layout.name,
    },
    mode: "onBlur",
    resolver: zodResolver(renameLayoutFormSchema),
  });

  const renameLayout = useUpdateLayout();

  const serverError = form.formState.errors.root?.serverErrors?.message;

  const onSubmit: SubmitHandler<RenameLayoutFormFields> = (data) => {
    return renameLayout
      .mutateAsync({
        layoutId: layout.layout_id,
        updates: { name: data.name },
      })
      .then(onSuccess);
  };

  return (
    <form
      onSubmit={(event) => {
        form
          .handleSubmit(onSubmit)(event)
          .catch((e) => {
            LoggerService.error("Failed to rename layout:", e);

            let errorMessage = "Failed to rename layout. Please try again.";

            // If it's a client error, we can show the user the message
            if (e instanceof RobotoDomainException && e.isClientError()) {
              errorMessage = e.message;
            }

            form.setError("root.serverErrors", {
              message: errorMessage,
            });
          });
      }}
    >
      <DialogTitle id="rename-layout-dialog-title" sx={{ pr: 6 }}>
        Rename layout
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={3}>
          <Typography variant="body1">
            Enter a new name for <b>&quot;{layout.name}&quot;</b>:
          </Typography>

          <TextField
            {...form.register("name")}
            autoFocus
            disabled={renameLayout.isPending}
            error={!!form.formState.errors.name}
            fullWidth
            helperText={form.formState.errors.name?.message}
            label="Layout name"
            variant="outlined"
            size="small"
          />
          {serverError && <Alert severity="error">{serverError}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 2, pt: 0 }}>
        <RobotoButton
          eventName="RenameLayoutCanceled"
          eventProperties={{
            layoutId: layout.layout_id,
            layoutName: layout.name,
          }}
          onClick={onClose}
          type="button"
          disabled={renameLayout.isPending}
        >
          Cancel
        </RobotoButton>
        <RobotoButton
          eventName="RenameLayoutClicked"
          eventProperties={() => ({
            layoutId: layout.layout_id,
            layoutName: layout.name,
            newName: form.getValues("name"),
          })}
          type="submit"
          variant="contained"
          disabled={renameLayout.isPending || !form.formState.isValid}
          endIcon={
            renameLayout.isPending ? (
              <CircularProgress size={16} color="inherit" />
            ) : undefined
          }
        >
          Rename Layout
        </RobotoButton>
      </DialogActions>
    </form>
  );
}
